import * as Actions from '../actions/redFlagTypeActions';
import RedFlagType from '../../entities/redFlagType';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type RedFlagTypeAction = { 
    type: string; 
    redFlagType: RedFlagType | null; 
    redFlagTypeId: string | RedFlagType; 
    partial: boolean; 
    lastFetch: number; 
    redFlagTypes: RedFlagType[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<RedFlagType> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {RedFlagType[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<RedFlagType> | null, firmId: string, userId: string, updates?: RedFlagType[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'redFlagType', 'redFlagTypes', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<RedFlagType>} data
 * @param {RedFlagType[]} [updates]
 * @returns {ArrayMap<RedFlagType>}
 */
function updateStorage(data: ArrayMap<RedFlagType>, updates?: RedFlagType[]): ArrayMap<RedFlagType> {
	if (logging) console.debug('⮂ redFlagTypeReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {RedFlagType[]} [updates]
 * @returns {ArrayMap<RedFlagType>}
 */
function updateStorageNoReturn(updates?: RedFlagType[]): void {
	if (logging) console.debug('⮂ redFlagTypeReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {RedFlagType[]} [data]
 * @returns {ArrayMap<RedFlagType>}
 */
function createState(data?: RedFlagType[] | ArrayMap<RedFlagType>): ArrayMap<RedFlagType> {
	if (!data) return ArrayMapRegistry.clone<RedFlagType>('redFlagType', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<RedFlagType>('redFlagType', data.map(e => e));
    else return ArrayMapRegistry.clone<RedFlagType>('redFlagType', data);
}

function redFlagTypes(state: ArrayMap<RedFlagType>, action: RedFlagTypeAction): ArrayMap<RedFlagType> {
	if (!state) state = createState();

	let st: ArrayMap<RedFlagType>;

	switch (action.type) {
		// action.redFlagType - the new value of redFlagType with id action.id
		// action.redFlagTypeId - id of the target, used if new value is null
		case Actions.REDFLAGTYPE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ redFlagTypeReducer: FETCH_ONE_SUCCESS', action);

			if (action.redFlagType === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.redFlagTypeId)) {
				st = state.clone();
				st.set(action.redFlagType);
				// state.clear();

				updateStorageNoReturn([action.redFlagType]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.redFlagTypes
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.redFlagTypes
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.redFlagTypes
			 !partial, !stateEmpty, fetchEmpty	-> D return redFlagTypes in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge redFlagTypes in state newer than those in action.redFlagTypes
		 */
		case Actions.REDFLAGTYPE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.redFlagTypes.length === 0;
			const fetched = action.redFlagTypes.map(redFlagType => new RedFlagType({...redFlagType, persisted: true}));

			if (logging) console.debug(`⮂ redFlagTypeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} redFlagTypes with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ redFlagTypeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ redFlagTypeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<RedFlagType> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: RedFlagType[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(redFlagType => {
					const current = st.get(redFlagType.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && redFlagType.status === 'D') {
							// Remove it from state
							toUpdate.push(redFlagType);
							st.delete(redFlagType);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (redFlagType.addedOn > current.addedOn) {
								toUpdate.push(redFlagType);
								st.set(redFlagType);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (redFlagType.status !== 'D') {
							toUpdate.push(redFlagType);
							st.set(redFlagType);
						}
						else if (logging) console.debug('⮂ redFlagTypeReducer: partialMerge: skipping deleted redFlagType not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ redFlagTypeReducer: empty: redFlagTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ redFlagTypeReducer: fullMerge: redFlagTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localRedFlagType => {
                    // If this redFlagType has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localRedFlagType.persisted) {
                        const fetchedRedFlagType = st.get(localRedFlagType.id);
                        if (!fetchedRedFlagType || localRedFlagType.addedOn > fetchedRedFlagType.addedOn) st.set(localRedFlagType);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.REDFLAGTYPE_FETCH_ONE_FAILURE:
			console.error('REDFLAGTYPE_FETCH_ONE_FAILURE', action.redFlagTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.REDFLAGTYPE_FETCH_FAILURE:
			console.error('REDFLAGTYPE_FETCH_FAILURE', action.redFlagTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.REDFLAGTYPE_RESTORE: {
		    let redFlagType = new RedFlagType({...action.redFlagType});
            st = state.clone();

            const existing = st.get(redFlagType.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (redFlagType.addedOn >= existing.addedOn) {
                    st.set(redFlagType);
                    return updateStorage(st, [redFlagType]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (redFlagType.status === 'D') return state;

                st.set(redFlagType);
                return updateStorage(st, [redFlagType]);
            }
        }
            
		case Actions.REDFLAGTYPE_SAVE: {
		    // console.debug('⮂ REDFLAGTYPE_SAVE reducer', action.redFlagType?.id);
		    
			// clone the redFlagType
			let redFlagType = new RedFlagType({...action.redFlagType});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new redFlagType in the state
			// If this is a create, add to the queue
			const _redFlagType = st.get(redFlagType.id);

			if (!_redFlagType) st.set(redFlagType);
			else {
				if (redFlagType.addedOn >= _redFlagType.addedOn) st.set(redFlagType);
			}

			return updateStorage(st, [redFlagType]);
		}

		case Actions.REDFLAGTYPE_SAVE_MANY: {
		    if (logging) console.debug('⮂ REDFLAGTYPE_SAVE_MANY reducer', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));
		    
			// clone the redFlagTypes
			let toProcess = action.redFlagTypes.map(redFlagType => new RedFlagType(redFlagType));

			st = state.clone();
			// state.clear();

			const updates: RedFlagType[] = [];

			toProcess.forEach(redFlagType => {
				// Add or update the new redFlagType in the state
				// If this is a create, add to the queue
				const existing = st.get(redFlagType.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (redFlagType.status !== 'D') {
						st.push(redFlagType);
						updates.push(redFlagType);
					}
					else if (logging) console.debug('⮂ REDFLAGTYPE_SAVE_MANY: skipping deleted redFlagType not in state');
				}
				else {
					if (redFlagType.addedOn >= existing.addedOn) {
						st.set(redFlagType);
						updates.push(redFlagType);
					}
					else if (logging) console.debug('⮂ Skipping, existing redFlagType had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REDFLAGTYPE_REMOVE: {
			// clone the redFlagType
			const redFlagType = new RedFlagType({...action.redFlagType});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(redFlagType);

			return updateStorage(st, [redFlagType]);
		}

		case Actions.REDFLAGTYPE_RESTORE_FAILURE:
		case Actions.REDFLAGTYPE_SAVE_FAILURE: {
		    console.warn('REDFLAGTYPE_SAVE_FAILURE reducer', action.redFlagType?.id);
		    if (!action.redFlagType?.id) {
				console.warn('REDFLAGTYPE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const redFlagType = ensure(RedFlagType, action.redFlagType);
			if (!redFlagType) {
				console.error('Could not find redFlagType after call to save');
				return state;
			}

			st = state.clone();

			const _redFlagTypeRef = state.get(redFlagType.id);

			if (_redFlagTypeRef && (_redFlagTypeRef.addedOn === redFlagType.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _redFlagType = new RedFlagType({..._redFlagTypeRef});
				_redFlagType.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_redFlagType.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('redFlagType', _redFlagType).then(r => {
						if (r.isErr()) console.error(`⮂ redFlagTypeReducer: failed to save redFlagType with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ redFlagTypeReducer: saved redFlagType with fatalError info');
					}).catch(err => console.error(`⮂ redFlagTypeReducer: failed to save redFlagType with fatalError info: ${err.message}`));

				st.set(_redFlagType);
			}

			return updateStorage(st);
		}

		case Actions.REDFLAGTYPE_SAVE_MANY_FAILURE: {
		    console.warn('REDFLAGTYPE_SAVE_MANY_FAILURE reducer', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.REDFLAGTYPE_RESTORE_SUCCESS:
		case Actions.REDFLAGTYPE_SAVE_SUCCESS: {
			if (!action.redFlagType?.id) {
				console.warn('REDFLAGTYPE_SAVE_SUCCESS reducer');
                return state;
			}

			const redFlagTypeRef = state.get(action.redFlagType?.id);
			if (!redFlagTypeRef) {
				console.warn('Could not find redFlagType after call to save');
                return state;
			}

			st = state.clone();

			if (action.redFlagType.addedOn >= redFlagTypeRef.addedOn) {
				st.set(action.redFlagType);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const redFlagType = new RedFlagType({...redFlagTypeRef, persisted: true});
				st.set(redFlagType);
			}

			return updateStorage(st, [action.redFlagType]);
		}

		case Actions.REDFLAGTYPE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ REDFLAGTYPE_SAVE_MANY_SUCCESS reducer', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const redFlagTypes: RedFlagType[] = [];

			action.redFlagTypes.forEach(e => {
				const redFlagTypeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!redFlagTypeRef) console.warn('Could not find redFlagType after call to save');
				else if (e.addedOn >= redFlagTypeRef.addedOn) {
				    st.set(e);
				    redFlagTypes.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const redFlagType = new RedFlagType({...redFlagTypeRef, persisted: true});
					st.set(redFlagType);
					redFlagTypes.push(redFlagType);
				}
			});

			return updateStorage(st, redFlagTypes);
		}

		case Actions.REDFLAGTYPE_REMOVE_FAILURE: {
		    console.warn('REDFLAGTYPE_REMOVE_FAILURE action', action.redFlagType?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.REDFLAGTYPE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.REDFLAGTYPE_RESTORE_MANY: {
			if (logging) console.debug('⮂ REDFLAGTYPE_RESTORE_MANY reducer', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));

			const toProcess = action.redFlagTypes.map(redFlagType => new RedFlagType({...redFlagType}));
			st = state.clone();

			const updates: RedFlagType[] = [];

			toProcess.forEach(redFlagType => {
				const existing = st.get(redFlagType.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (redFlagType.addedOn >= existing.addedOn) {
						st.set(redFlagType);
						updates.push(redFlagType);
					}
					else if (logging) console.debug('⮂ REDFLAGTYPE_RESTORE_MANY: skipping older version of redFlagType');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (redFlagType.status !== 'D') {
						st.set(redFlagType);
						updates.push(redFlagType);
					}
					else if (logging) console.debug('⮂ REDFLAGTYPE_RESTORE_MANY: skipping deleted redFlagType not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REDFLAGTYPE_REMOVE_MANY: {
			if (logging) console.debug('⮂ REDFLAGTYPE_REMOVE_MANY reducer', action.redFlagTypes.map(redFlagType => redFlagType.id).join(','));

			const toProcess = action.redFlagTypes.map(redFlagType => new RedFlagType({...redFlagType}));
			st = state.clone();

			toProcess.forEach(redFlagType => {
				st.delete(redFlagType);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default redFlagTypes;